// src/components/payments/AddPaymentMethodDialog.tsx
//
// The add-a-card flow, as four explicit steps.
//
//   1. CRM backend starts a provisioning session (server holds the merchant)
//   2. the browser runs the protocol PAYMENTS selected
//   3. the browser finalizes DIRECTLY with Payments
//   4. the browser hands CRM the canonical paymentInstrumentId
//
// ============================================================================
// STEP 2 IS PROTOCOL EXECUTION, NOT PROCESSOR CHOICE
// ============================================================================
//
// The switch on `browserAction.kind` runs whichever protocol Payments already
// chose. There is no Stripe/NMI selector anywhere in this component, no
// `preferredProcessor`, and no way for a user or a developer to express one —
// Payments refuses such a hint at the contract level, and offering the choice
// in the UI would be a lie about what the button does.
//
// ============================================================================
// CARD DATA NEVER REACHES CRM
// ============================================================================
//
// In production, step 2 mounts the provider's own browser component and the
// customer types their PAN and CVC into THAT. This component never renders a
// card field, and the provider's single-use token goes from the browser
// straight to Payments in step 3. Nothing card-shaped is sent to, logged by, or
// stored in CRM.
//
// `tokenize_with_public_key` is now implemented: `HostedCardFields` mounts the
// gateway's own iframes and returns the single-use token it issues.
// `confirm_setup_with_client_secret` is not — that protocol needs a different
// vendor script, and no connection in this environment produces it. It reports
// itself as unavailable rather than rendering a step that cannot complete.
//
// `onProviderResult` remains as an injection point so tests can drive the flow
// without a network-loaded script. When it is absent, the real step renders.
import React, { useCallback, useEffect, useState } from 'react';
import {
  useAssociateInstrumentMutation,
  useStartProvisioningSessionMutation,
  type ProvisioningBrowserAction,
} from '../../api/paymentsIntegrationApi';
import { describeBrowserAction, finalizeProvisioning } from '../../lib/paymentsProvisioning';
import { HostedCardFields } from './HostedCardFields';
import type { TokenizationOutcome } from '../../lib/collectJsTokenizer';

type Phase =
  | { name: 'starting' }
  | { name: 'provider'; action: ProvisioningBrowserAction; authority: string }
  | { name: 'finalizing' }
  | { name: 'saved' }
  | { name: 'requires_attention' }
  | { name: 'error'; message: string };

/** Maps a backend refusal onto copy a person can act on. */
function messageFor(reason: unknown): string {
  const code = typeof reason === 'string' ? reason : '';
  switch (code) {
    case 'provisioning_disabled':
      return 'Saving payment methods is not switched on for this environment yet.';
    case 'merchant_not_configured':
      return 'This workspace is not set up to accept payments yet.';
    case 'contact_not_found':
      return 'That contact could not be found in this workspace.';
    case 'payments_unavailable':
      return 'The payment service is temporarily unavailable. Please try again shortly.';
    default:
      return 'We could not start the payment setup. Please try again.';
  }
}

export interface AddPaymentMethodDialogProps {
  contactId: string;
  onClose: () => void;
  /**
   * The provider browser step, injected.
   *
   * Returns the ephemeral token for `tokenize_with_public_key`, or undefined
   * for `confirm_setup_with_client_secret` (where the provider confirms its own
   * setup object and there is no token to carry). Tests supply a fake; a future
   * PR supplies the real SDK mount.
   */
  onProviderResult?: (
    action: ProvisioningBrowserAction
  ) => Promise<{ paymentToken?: string } | null>;
}

export function AddPaymentMethodDialog({
  contactId,
  onClose,
  onProviderResult,
}: AddPaymentMethodDialogProps): React.ReactElement {
  const [phase, setPhase] = useState<Phase>({ name: 'starting' });
  const [startSession] = useStartProvisioningSessionMutation();
  const [associate] = useAssociateInstrumentMutation();

  const begin = useCallback(async () => {
    setPhase({ name: 'starting' });
    const result = await startSession({ contactId });

    if ('error' in result) {
      const data = (result.error as { data?: { error?: unknown } })?.data;
      setPhase({ name: 'error', message: messageFor(data?.error) });
      return;
    }

    // The authority is held in component state for the life of this flow and
    // is never logged, never persisted, and never sent back to CRM.
    setPhase({
      name: 'provider',
      action: result.data.browserAction,
      authority: result.data.provisioningAuthority,
    });
  }, [contactId, startSession]);

  useEffect(() => {
    void begin();
  }, [begin]);

  /**
   * Finalize with Payments and link the result to the contact.
   *
   * Shared by both the injected step and the real hosted-card step so there is
   * exactly ONE place that decides what a finalization outcome means. The
   * token is passed straight through and never held.
   */
  const finalize = useCallback(
    async (authority: string, paymentToken: string | undefined) => {
      setPhase({ name: 'finalizing' });

      // DIRECT to Payments. The token — when there is one — goes browser →
      // Payments and is not returned, stored or logged here.
      const finalized = await finalizeProvisioning({
        provisioningAuthority: authority,
        ...(paymentToken ? { paymentToken } : {}),
      });

      if (!finalized.ok) {
        if (finalized.reason === 'requires_attention') {
          // NO association, NO automatic restart, NO provider switch, and
          // emphatically not "Card saved."
          setPhase({ name: 'requires_attention' });
          return;
        }
        setPhase({
          name: 'error',
          message:
            finalized.reason === 'unavailable'
              ? 'The payment service is temporarily unavailable. Please try again shortly.'
              : 'That payment method could not be saved.',
        });
        return;
      }

      // 201 and 200 are BOTH success and may name the SAME instrument — a
      // replayed finalization converges rather than creating a second one. The
      // association call is likewise idempotent, so this is safe either way.
      const associated = await associate({
        contactId,
        paymentInstrumentId: finalized.paymentInstrumentId,
      });

      if ('error' in associated) {
        setPhase({
          name: 'error',
          message: 'The payment method was set up but could not be linked to this contact.',
        });
        return;
      }

      setPhase({ name: 'saved' });
    },
    [associate, contactId]
  );

  /** The gateway answered. A refusal here is a card problem, not a Payments one. */
  const onTokenized = useCallback(
    async (outcome: TokenizationOutcome, authority: string) => {
      if (!outcome.ok) {
        if (outcome.reason === 'cancelled') {
          onClose();
          return;
        }
        setPhase({
          name: 'error',
          message:
            outcome.reason === 'invalid_card'
              ? 'Those card details could not be accepted. Please check them and try again.'
              : 'The secure card form is unavailable right now. Please try again shortly.',
        });
        return;
      }
      await finalize(authority, outcome.paymentToken);
    },
    [finalize, onClose]
  );

  const runProviderStep = useCallback(
    async (action: ProvisioningBrowserAction, authority: string) => {
      if (!onProviderResult) {
        setPhase({
          name: 'error',
          message: 'The payment form is not available in this build.',
        });
        return;
      }

      const providerResult = await onProviderResult(action);
      if (providerResult === null) {
        onClose();
        return;
      }

      await finalize(authority, providerResult.paymentToken);
    },
    [finalize, onClose, onProviderResult]
  );

  return (
    <div
      className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4"
      role="dialog"
      aria-label="Add payment method"
      data-testid="add-payment-method-dialog"
    >
      {phase.name === 'starting' && <p className="text-sm text-slate-600">Setting up…</p>}

      {phase.name === 'provider' && (
        <div data-testid="provider-step" data-action-kind={phase.action.kind}>
          <p className="text-sm text-slate-700">{describeBrowserAction(phase.action)}</p>

          {onProviderResult ? (
            // Injected step — tests and any caller supplying its own mount.
            <button
              type="button"
              className="mt-3 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white"
              onClick={() => void runProviderStep(phase.action, phase.authority)}
              data-testid="provider-continue"
            >
              Continue
            </button>
          ) : phase.action.kind === 'tokenize_with_public_key' ? (
            <HostedCardFields
              publicKey={phase.action.publicKey}
              onResult={(outcome) => void onTokenized(outcome, phase.authority)}
              onCancel={onClose}
            />
          ) : (
            // The other protocol needs a vendor script this build does not
            // load. Saying so beats rendering a step that cannot finish.
            <p className="mt-3 text-sm text-rose-700" data-testid="protocol-unavailable">
              This payment setup method is not available in this build.
            </p>
          )}
        </div>
      )}

      {phase.name === 'finalizing' && <p className="text-sm text-slate-600">Saving…</p>}

      {phase.name === 'saved' && (
        <div data-testid="saved">
          <p className="text-sm font-medium text-emerald-700">Payment method saved.</p>
          <button type="button" className="mt-2 text-xs text-slate-600 underline" onClick={onClose}>
            Close
          </button>
        </div>
      )}

      {phase.name === 'requires_attention' && (
        <div data-testid="requires-attention">
          <p className="text-sm font-medium text-amber-800">We could not confirm this setup.</p>
          <p className="mt-1 text-xs text-amber-800">
            The payment method may or may not have been saved. Please check before trying again —
            we have not linked anything to this contact.
          </p>
          <button type="button" className="mt-2 text-xs text-slate-600 underline" onClick={onClose}>
            Close
          </button>
        </div>
      )}

      {phase.name === 'error' && (
        <div data-testid="error">
          <p className="text-sm text-rose-700">{phase.message}</p>
          <button type="button" className="mt-2 text-xs text-slate-600 underline" onClick={onClose}>
            Close
          </button>
        </div>
      )}
    </div>
  );
}
